import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faPlus, faTimes, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Swal from 'sweetalert2';
import ToggleSwitch from 'react-switch';

import { blackberry, coolGrey, grape, greyStroke, salmon, squidInk, white } from 'lib/css/colors';
import { GLOBAL_MANUFACTURERS_UPDATE_FROM_FSA } from 'lib/networking/endpoints';
import { ManufacturerVerificationStatus } from 'lib/enums/ManufacturerVerificationStatus';
import usePost from 'lib/hooks/usePost';

function ManufacturerEditModal({ manufacturer, show, onHide, refetch }) {
  const [name, setName] = useState(manufacturer.name);
  const [manufacturerGLNs, setManufacturerGLNs] = useState(
    manufacturer.manufacturer_glns
      ? Object.entries(manufacturer.manufacturer_glns).map(([gln, subCompanyName]) => ({
          gln,
          sub_company_name: subCompanyName || '',
        }))
      : [],
  );
  const [verificationStatus, setVerificationStatus] = useState(
    manufacturer.verification_status === ManufacturerVerificationStatus.VERIFIED,
  );
  const [requiresManufacturerPortal, setRequiresManufacturerPortal] = useState(
    manufacturer.requires_manufacturer_portal,
  );

  const verified = manufacturer.verification_status === ManufacturerVerificationStatus.VERIFIED;

  const formatGLNsForSubmission = () => {
    const glnsObject = {};
    manufacturerGLNs.forEach(entry => {
      if (entry.gln.trim()) {
        glnsObject[entry.gln.trim()] = entry.sub_company_name || null;
      }
    });
    return glnsObject;
  };

  const { postData: updateManufacturerDetails, loading: updating } = usePost(
    GLOBAL_MANUFACTURERS_UPDATE_FROM_FSA,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Manufacturer details updated successfully',
        title: 'Success',
        timer: 500,
      }).then(() => {
        onHide();
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `Error occurred on updating manufacturer details: ${error?.response?.data?.message}`,
        title: 'Error',
      }),
  );

  const addNewGLNEntry = () => {
    setManufacturerGLNs([...manufacturerGLNs, { gln: '', sub_company_name: '' }]);
  };

  const removeGLNEntry = index => {
    const updatedGLNs = [...manufacturerGLNs];
    updatedGLNs.splice(index, 1);
    setManufacturerGLNs(updatedGLNs);
  };

  const updateGLNEntry = (index, field, value) => {
    const updatedGLNs = [...manufacturerGLNs];
    updatedGLNs[index][field] = value;
    setManufacturerGLNs(updatedGLNs);
  };

  return (
    <Modal animation={false} centered show={show} onHide={onHide} size="lg">
      <div className={css(styles.modalContent)}>
        <Modal.Header className={css(styles.header)}>
          <Modal.Title className={css(styles.title)}>Edit Manufacturer</Modal.Title>
          <Button
            variant="link"
            className={css(styles.closeButton)}
            onClick={onHide}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </Modal.Header>
        <Modal.Body className={css(styles.body)}>
          <Row>
            <Col>
              <Form.Group className={css(styles.formGroup)}>
                <Form.Label className={css(styles.label)}>Manufacturer Name</Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  disabled={verified}
                  className={css(styles.input)}
                />
              </Form.Group>

              <Form.Group className={css(styles.formGroup)}>
                <Form.Label className={css(styles.label)}>Verification Status</Form.Label>
                <div>
                  <ToggleSwitch
                    onColor={grape}
                    checked={verificationStatus}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    onChange={() => setVerificationStatus(!verificationStatus)}
                    disabled={verified}
                  />
                </div>
              </Form.Group>

              <Form.Group className={css(styles.formGroup)}>
                <Form.Label className={css(styles.label)}>Manufacturer GLNs</Form.Label>
                {manufacturerGLNs.map((entry, index) => (
                  <Row key={index} className={css(styles.glnRow)}>
                    <Col xs={5}>
                      <Form.Control
                        type="text"
                        placeholder="GLN"
                        value={entry.gln}
                        onChange={e => updateGLNEntry(index, 'gln', e.target.value)}
                        disabled={verified}
                        className={css(styles.input)}
                      />
                    </Col>
                    <Col xs={5}>
                      <Form.Control
                        type="text"
                        placeholder="Sub-company name"
                        value={entry.sub_company_name}
                        onChange={e => updateGLNEntry(index, 'sub_company_name', e.target.value)}
                        disabled={verified}
                        className={css(styles.input)}
                      />
                    </Col>
                    <Col xs={2} className="d-flex align-items-center">
                      <Button
                        variant="link"
                        className={css(styles.removeGLNButton)}
                        onClick={() => removeGLNEntry(index)}
                        disabled={verified || manufacturerGLNs.length <= 1}
                        aria-label="Remove GLN"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </Button>
                    </Col>
                  </Row>
                ))}
                <Button
                  className={css(styles.addGLNButton)}
                  onClick={addNewGLNEntry}
                  disabled={verified}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  Add GLN
                </Button>
              </Form.Group>

              <Form.Group className={css(styles.formGroup)}>
                <Form.Label className={css(styles.label)}>
                  Creation of manufacturer portal is required
                </Form.Label>
                <div>
                  <ToggleSwitch
                    onColor={grape}
                    checked={requiresManufacturerPortal}
                    checkedIcon={false}
                    uncheckedIcon={false}
                    onChange={() => setRequiresManufacturerPortal(!requiresManufacturerPortal)}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className={css(styles.footer)}>
          <Button
            className={css(styles.primaryButton)}
            onClick={() =>
              updateManufacturerDetails({
                id: manufacturer.id,
                name: name,
                verification_status: verificationStatus
                  ? ManufacturerVerificationStatus.VERIFIED
                  : ManufacturerVerificationStatus.UNVERIFIED,
                manufacturer_glns: formatGLNsForSubmission(),
                requires_manufacturer_portal: requiresManufacturerPortal,
              })
            }
            disabled={updating}
          >
            {updating ? <Spinner animation="border" role="status" size="sm" /> : 'Update'}
          </Button>
          <Button className={css(styles.cancelButton)} onClick={onHide}>
            Cancel
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    backgroundColor: white,
    borderBottom: `1px solid ${greyStroke}`,
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: squidInk,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: coolGrey,
    fontSize: 18,
    cursor: 'pointer',
    padding: 8,
    borderRadius: 6,
    ':hover': {
      backgroundColor: `${grape}15`,
      color: grape,
    },
  },
  body: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    color: squidInk,
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    border: `1px solid ${greyStroke}`,
    padding: '10px 14px',
    ':focus': {
      borderColor: grape,
      boxShadow: `0 0 0 1px ${grape}`,
    },
  },
  glnRow: {
    marginBottom: 8,
  },
  addGLNButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px dashed ${grape}`,
    borderRadius: 8,
    padding: '8px 16px',
    fontWeight: 600,
    fontSize: 13,
    marginTop: 8,
    ':hover': {
      backgroundColor: `${grape}15`,
    },
    ':disabled': {
      borderColor: greyStroke,
      color: coolGrey,
      backgroundColor: 'transparent',
    },
  },
  removeGLNButton: {
    background: 'none',
    border: 'none',
    color: salmon,
    cursor: 'pointer',
    padding: 6,
    borderRadius: 4,
    ':hover': {
      backgroundColor: `${salmon}15`,
    },
    ':disabled': {
      color: greyStroke,
    },
  },
  footer: {
    backgroundColor: white,
    borderTop: `1px solid ${greyStroke}`,
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 12,
  },
  primaryButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: grape,
    },
    ':disabled': {
      backgroundColor: greyStroke,
      color: coolGrey,
    },
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px solid ${grape}`,
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: `${grape}15`,
    },
  },
});

ManufacturerEditModal.propTypes = {
  manufacturer: PropTypes.object,
  show: PropTypes.bool,
  onHide: PropTypes.func,
  refetch: PropTypes.func,
};

export default ManufacturerEditModal;
